// import React from 'react'
import {
  CircularProgress,
  InputAdornment,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ThemeProvider,
  createTheme,
  Button,
  Checkbox,
} from "@mui/material";
import searchIcon from "../../assets/searchIcon.svg";
import eyeIcon from "../../assets/redesign/eye.svg";

import {
  SetStateAction,
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
// import { baseUrl } from "../Utils/common";
// import { useNavigate } from "react-router-dom";
// import { useSnackbar } from "../../context/SnackbarContext";
import axios from "axios";
import "./SearchCv.css";
import LongText from "../../Components/LongText/LongText";
import SecondaryHeader from "../../Components/Header/SecondaryHeader";
import { useLocation, useNavigate } from "react-router-dom";
import { StateItem, EducationItem } from "../../models/master";
import { useMasterData } from "../../context/MasterContextProvider";

// Cache search results so we don't refetch when returning from View Profile
const searchCache: { key: string; data: any } = { key: "", data: null };

/** Align cache with auth + region: same filters for a new user/region must miss cache. */
const getSearchCacheScopeFromStorage = () => {
  const userId = localStorage.getItem("userId") || "";
  let regionCountryId = "";
  try {
    const raw = localStorage.getItem("region");
    if (raw) {
      const parsed = JSON.parse(raw);
      regionCountryId = parsed?.countryId ?? "";
    }
  } catch {
    /* ignore */
  }
  return `${userId}|${regionCountryId}`;
};

const getSearchCacheKey = (params: {
  page: number;
  minYears?: number;
  maxYears?: number;
  locations: string[];
  education: string[];
  keyword?: string;
  cacheScope: string;
}) =>
  JSON.stringify({
    scope: params.cacheScope,
    page: params.page,
    minYears: params.minYears,
    maxYears: params.maxYears,
    locations: params.locations.sort(),
    education: params.education.sort(),
    keyword: params.keyword || "",
  });

const SearchCv = () => {
  // const { showSnackbar } = useSnackbar();
  // const navigate = useNavigate();

  const location = useLocation();
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const passedQuery = queryParams.get("searchQuery");
  const passedLocations = useMemo(
    () => queryParams.getAll("locations"),
    [queryParams]
  ); // Memoize to prevent recreating array
  const passedMinYears = queryParams.get("minYears");
  const passedMaxYears = queryParams.get("maxYears");
  const passedPage = queryParams.get("page");
  const passedEducation = useMemo(
    () => queryParams.getAll("education"),
    [queryParams]
  );

  const [searchData, setSearchData] = useState<any>();
  const [searchQuery, setSearchQuery] = useState<string>(passedQuery || "");

  const [currentPage, setCurrentPage] = useState(
    () => (passedPage ? parseInt(passedPage, 10) : 1) || 1
  );
  const rowsPerPage = 10;

  const { states } = useMasterData();

  // Update searchQuery when URL param changes
  useEffect(() => {
    if (passedQuery) {
      setSearchQuery(passedQuery);
    }
  }, [passedQuery]);

  // const CompanyId = localStorage.getItem("companyId");

  const handleSearchChange = (event: any) => {
    const query = event.target.value;
    setSearchQuery(query);
  };

  const router = useNavigate();

  const [isLoading, setIsLoading] = useState(false);

  const [education, setEducation] = useState<EducationItem[]>([]);

  // Years filter state (same as JobApplicationsView)
  const [minYearsExperience, setMinYearsExperience] = useState<number>(0);
  const [maxYearsExperience, setMaxYearsExperience] = useState<number>(50);
  const [tempMinYears, setTempMinYears] = useState<number>(0);
  const [tempMaxYears, setTempMaxYears] = useState<number>(50);
  const [isYearsDropdownOpen, setIsYearsDropdownOpen] = useState(false);
  const yearsDropdownRef = useRef<HTMLDivElement>(null);

  // Custom dropdown states for consistent styling
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);
  // const [isEducationDropdownOpen, setIsEducationDropdownOpen] = useState(false);
  const locationDropdownRef = useRef<HTMLDivElement>(null);
  // const educationDropdownRef = useRef<HTMLDivElement>(null);
  // Education options
  const educationOptions = [
    { id: "High", text: "High School Education" },
    { id: "Associate", text: "Associate Degree" },
    { id: "Bachelors", text: "Bachelor's Degree" },
    { id: "Masters", text: "Master's Degree" },
    { id: "PHD", text: "Doctorate (PH.D.)" },
  ];

  // Years filter helper functions (same as JobApplicationsView)
  const getYearsDisplayText = () => {
    if (minYearsExperience === 0 && maxYearsExperience === 50) {
      return "All years of experience";
    }
    if (minYearsExperience === 0) {
      return `Up to ${maxYearsExperience} years`;
    }
    if (maxYearsExperience === 50) {
      return `${minYearsExperience}+ years`;
    }
    return `${minYearsExperience}-${maxYearsExperience} years`;
  };

  const handleYearsReset = () => {
    setTempMinYears(0);
    setTempMaxYears(50);
    setMinYearsExperience(0);
    setMaxYearsExperience(50);
    setIsYearsDropdownOpen(false);
  };

  const handleYearsApply = () => {
    setMinYearsExperience(tempMinYears);
    setMaxYearsExperience(tempMaxYears);
    setIsYearsDropdownOpen(false);
  };

  const handleYearsDropdownToggle = () => {
    if (isYearsDropdownOpen) {
      setIsYearsDropdownOpen(false);
    } else {
      setTempMinYears(minYearsExperience);
      setTempMaxYears(maxYearsExperience);
      setIsYearsDropdownOpen(true);
    }
  };

  // Location filter helper functions
  const getLocationDisplayText = () => {
    if (selectedLocation.length === 0) {
      return "All Locations";
    }
    if (selectedLocation.length === 1) {
      return selectedLocation[0].stateName;
    }
    return `${selectedLocation.length} Locations selected`;
  };

  const handleLocationSelect = (location: StateItem) => {
    const isSelected = selectedLocation.some((loc) => loc.id === location.id);
    if (isSelected) {
      setSelectedLocation(
        selectedLocation.filter((loc) => loc.id !== location.id)
      );
    } else {
      setSelectedLocation([...selectedLocation, location]);
    }
  };

  // Education filter helper functions
  // const getEducationDisplayText = () => {
  //   if (education.length === 0) {
  //     return "All Education";
  //   }
  //   if (education.length === 1) {
  //     return education[0].text;
  //   }
  //   return `${education.length} Education levels selected`;
  // };

  // const handleEducationSelect = (educationItem: EducationItem) => {
  //   const isSelected = education.some((edu) => edu.id === educationItem.id);
  //   if (isSelected) {
  //     setEducation(education.filter((edu) => edu.id !== educationItem.id));
  //   } else {
  //     setEducation([...education, educationItem]);
  //   }
  // };

  const [selectedLocation, setSelectedLocation] = useState<StateItem[]>([]);

  const buildSearchReturnUrl = () => {
    const params = new URLSearchParams();
    if (searchQuery.trim()) params.set("searchQuery", searchQuery.trim());
    selectedLocation.forEach((loc) => params.append("locations", loc.id));
    education.forEach((edu) => params.append("education", edu.id));
    if (minYearsExperience > 0) params.set("minYears", String(minYearsExperience));
    if (maxYearsExperience < 50) params.set("maxYears", String(maxYearsExperience));
    if (currentPage > 1) params.set("page", String(currentPage));
    const query = params.toString();
    return `/searchcv${query ? `?${query}` : ""}`;
  };

  const handleViewProfile = (id: string) => {
    router(`/dashboard/profile?id=${id}&from=${encodeURIComponent(buildSearchReturnUrl())}`);
  };

  const searchAllData = useCallback(async () => {
    const formattedLocation: string[] = selectedLocation.map((option) => option.id);
    const formattedEducation: string[] = education.map((option) => option.id);
    const cacheKey = getSearchCacheKey({
      page: currentPage,
      minYears: minYearsExperience > 0 ? minYearsExperience : undefined,
      maxYears: maxYearsExperience < 50 ? maxYearsExperience : undefined,
      locations: formattedLocation,
      education: formattedEducation,
      keyword: searchQuery.trim() || undefined,
      cacheScope: getSearchCacheScopeFromStorage(),
    });

    if (searchCache.key === cacheKey && searchCache.data) {
      setSearchData(searchCache.data);
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_BASE_URL}/candidate/profile/search/enhanced`,
        {
          page: currentPage,
          limit: rowsPerPage,
          filters: {
            minYears: minYearsExperience > 0 ? minYearsExperience : undefined,
            maxYears: maxYearsExperience < 50 ? maxYearsExperience : undefined,
            stateId:
              formattedLocation.length > 0 ? formattedLocation : undefined,
            qualificationType:
              formattedEducation.length > 0 ? formattedEducation : undefined,
          },
          keyword: searchQuery.trim() || undefined,
          companyId: localStorage.getItem("companyId") || "",
        },
        {
          headers: {
            Authorization: localStorage.getItem("userId"),
          },
        }
      );

      if (response.data.statusCode === "OK") {
        searchCache.key = cacheKey;
        searchCache.data = response.data;
        setSearchData(response.data);
      } else {
        console.log(response.data.message);
      }
    } catch (error) {
      console.error("Error during search:", error);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    minYearsExperience,
    maxYearsExperience,
    selectedLocation,
    education,
    searchQuery,
  ]);

  // Initialize filters from URL parameters (defer until states ready when locations needed - single batch)
  useEffect(() => {
    if (passedLocations.length > 0 && (!states || states.length === 0)) {
      return;
    }

    // Set location filter if passed
    if (passedLocations.length > 0 && states) {
      const matchingStates = states.filter((state) =>
        passedLocations.includes(state.id)
      );
      if (matchingStates.length > 0) {
        setSelectedLocation(matchingStates);
      }
    }

    // Set education filter if passed
    if (passedEducation.length > 0) {
      const matchingEducation = educationOptions.filter((opt) =>
        passedEducation.includes(opt.id)
      );
      if (matchingEducation.length > 0) {
        setEducation(matchingEducation);
      }
    }

    // Set years filter if passed
    if (passedMinYears || passedMaxYears) {
      const minYears = parseInt(passedMinYears || "0");
      const maxYears = parseInt(passedMaxYears || "50");

      setMinYearsExperience(minYears);
      setMaxYearsExperience(maxYears);
      setTempMinYears(minYears);
      setTempMaxYears(maxYears);
    }
  }, [passedLocations, passedMinYears, passedMaxYears, passedEducation, states]);

  // Single debounced search - batches mount, URL params, and filter changes into one call
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchAllData();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [
    searchAllData,
  ]);

  // Click outside handlers for all dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        yearsDropdownRef.current &&
        !yearsDropdownRef.current.contains(event.target as Node)
      ) {
        setIsYearsDropdownOpen(false);
      }
      if (
        locationDropdownRef.current &&
        !locationDropdownRef.current.contains(event.target as Node)
      ) {
        setIsLocationDropdownOpen(false);
      }
      // if (
      //   educationDropdownRef.current &&
      //   !educationDropdownRef.current.contains(event.target as Node)
      // ) {
      //   setIsEducationDropdownOpen(false);
      // }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handlePageChange = (_event: any, page: SetStateAction<number>) => {
    setCurrentPage(page);
  };

  const theme = createTheme();

  return (
    <>
      <SecondaryHeader
        title={`Total talents: ${searchData?.data?.totalRecords || 0}`}
      />
      <div className="main-inner-box">
        <div className="main_table_wrapper">
          <TextField
            type="search"
            id="search"
            className="search-input"
            value={searchQuery}
            size="small"
            style={{ opacity: 1, width: "100%" }}
            onChange={handleSearchChange}
            placeholder="Search for name or job title... Use 'AND', 'OR', 'NOT' for more specific results"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <img src={searchIcon} />
                </InputAdornment>
              ),
            }}
          />
          <br />
          <div className="jobs_filters_section">
            <p className="">Filters</p>
            <div className="filters_wrapper">
              <div className="filter-container" ref={yearsDropdownRef}>
                <div
                  className={`filter-dropdown ${
                    isYearsDropdownOpen ? "active" : ""
                  }`}
                  onClick={handleYearsDropdownToggle}
                >
                  <span className="filter-text">{getYearsDisplayText()}</span>
                  <svg
                    className={`filter-arrow ${
                      isYearsDropdownOpen ? "open" : ""
                    }`}
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>

                {isYearsDropdownOpen && (
                  <div className="years-filter-dropdown-menu">
                    <div className="years-filter-content">
                      <div className="years-filter-section">
                        <label className="years-filter-label">From</label>
                        <input
                          type="number"
                          value={tempMinYears === 0 ? "" : tempMinYears}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Only allow empty string or valid numbers
                            if (
                              value === "" ||
                              (/^\d+$/.test(value) &&
                                Number(value) >= 0 &&
                                Number(value) <= 50)
                            ) {
                              setTempMinYears(value === "" ? 0 : Number(value));
                            }
                          }}
                          onKeyDown={(e) => {
                            // Prevent non-numeric characters
                            if (
                              !/[0-9]/.test(e.key) &&
                              e.key !== "Backspace" &&
                              e.key !== "Delete" &&
                              e.key !== "Tab" &&
                              e.key !== "Enter" &&
                              e.key !== "ArrowLeft" &&
                              e.key !== "ArrowRight"
                            ) {
                              e.preventDefault();
                            }
                          }}
                          onBlur={(e) => {
                            const value = Math.max(
                              0,
                              Math.min(50, Number(e.target.value) || 0)
                            );
                            setTempMinYears(value);
                          }}
                          placeholder="0"
                          min="0"
                          max="50"
                          className="years-filter-input"
                        />
                      </div>

                      <div className="years-filter-section">
                        <label className="years-filter-label">To</label>
                        <input
                          type="number"
                          value={tempMaxYears === 50 ? "" : tempMaxYears}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Only allow empty string or valid numbers
                            if (
                              value === "" ||
                              (/^\d+$/.test(value) &&
                                Number(value) >= 0 &&
                                Number(value) <= 50)
                            ) {
                              setTempMaxYears(
                                value === "" ? 50 : Number(value)
                              );
                            }
                          }}
                          onKeyDown={(e) => {
                            // Prevent non-numeric characters
                            if (
                              !/[0-9]/.test(e.key) &&
                              e.key !== "Backspace" &&
                              e.key !== "Delete" &&
                              e.key !== "Tab" &&
                              e.key !== "Enter" &&
                              e.key !== "ArrowLeft" &&
                              e.key !== "ArrowRight"
                            ) {
                              e.preventDefault();
                            }
                          }}
                          onBlur={(e) => {
                            const value = Math.max(
                              tempMinYears,
                              Math.min(50, Number(e.target.value) || 50)
                            );
                            setTempMaxYears(value);
                          }}
                          placeholder="50"
                          min={tempMinYears.toString()}
                          max="50"
                          className="years-filter-input"
                        />
                      </div>

                      <Button
                        variant="contained"
                        onClick={handleYearsApply}
                        sx={{
                          backgroundColor: "#10b981",
                          color: "white",
                          textTransform: "none",
                          fontWeight: "600",
                          padding: "10px 24px",
                          borderRadius: "8px",
                          marginTop: "4px",
                          "&:hover": {
                            backgroundColor: "#059669",
                          },
                        }}
                      >
                        Apply
                      </Button>

                      <button
                        className="years-filter-reset-btn"
                        onClick={handleYearsReset}
                      >
                        Reset
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Location Filter */}
              <div className="filter-container" ref={locationDropdownRef}>
                <div
                  className={`filter-dropdown ${
                    isLocationDropdownOpen ? "active" : ""
                  }`}
                  onClick={() =>
                    setIsLocationDropdownOpen(!isLocationDropdownOpen)
                  }
                >
                  <span className="filter-text">
                    {getLocationDisplayText()}
                  </span>
                  <svg
                    className={`filter-arrow ${
                      isLocationDropdownOpen ? "open" : ""
                    }`}
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>

                {isLocationDropdownOpen && (
                  <div className="filter-dropdown-menu">
                    <div className="filter-options">
                      {states?.map((state) => (
                        <div
                          key={state.id}
                          className="filter-option"
                          onClick={() => handleLocationSelect(state)}
                        >
                          <Checkbox
                            checked={selectedLocation.some(
                              (loc) => loc.id === state.id
                            )}
                            size="small"
                            style={{ color: "rgba(70, 214, 79, 1)" }}
                          />
                          {state.stateName}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Education Filter */}
              {/* <div className="filter-container" ref={educationDropdownRef}>
                <div
                  className={`filter-dropdown ${
                    isEducationDropdownOpen ? "active" : ""
                  }`}
                  onClick={() =>
                    setIsEducationDropdownOpen(!isEducationDropdownOpen)
                  }
                >
                  <span className="filter-text">
                    {getEducationDisplayText()}
                  </span>
                  <svg
                    className={`filter-arrow ${
                      isEducationDropdownOpen ? "open" : ""
                    }`}
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>

                {isEducationDropdownOpen && (
                  <div className="filter-dropdown-menu">
                    <div className="filter-options">
                      {educationOptions.map((option) => (
                        <div
                          key={option.id}
                          className="filter-option"
                          onClick={() => handleEducationSelect(option)}
                        >
                          <Checkbox
                            checked={education.some(
                              (edu) => edu.id === option.id
                            )}
                            size="small"
                            style={{ color: "rgba(70, 214, 79, 1)" }}
                          />
                          {option.text}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div> */}
            </div>
          </div>
          <br />
          {isLoading ? (
            <div className="table_loader_ak">
              <CircularProgress size={30} color="inherit" />
            </div>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="jobs table">
                  <TableHead>
                    <TableRow className="main_table_row_bg">
                      <TableCell>
                        <p className="bold_heading_table">Name</p>
                      </TableCell>
                      <TableCell align="left">
                        <p className="bold_heading_table">Job Title</p>
                      </TableCell>
                      <TableCell align="left">
                        <p className="bold_heading_table actions width">
                          Actions
                        </p>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {searchData?.data?.profilesList?.map(
                      (row: any, idx: number) => (
                        <TableRow
                          onClick={() => {
                            // setCurrentIndex(idx);
                            handleViewProfile(row.profileId);
                          }}
                          className="table_cell_hover pointer"
                          key={idx}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell component="th" scope="row">
                            <LongText
                              title={row.displayName || "N/A"}
                              cutoff={30}
                            />
                          </TableCell>
                          <TableCell align="left">
                            <LongText
                              title={row.jobTitle || "N/A"}
                              cutoff={30}
                            />{" "}
                          </TableCell>
                          <TableCell align="left">
                            <div className="view_profile_btn">
                              <img src={eyeIcon} alt="eye_icon" />
                              <p>View Profile</p>
                            </div>
                          </TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <div className="pagination-container">
                <div className="header-desc">
                  Showing {(currentPage - 1) * rowsPerPage + 1} to{" "}
                  {Math.min(
                    currentPage * rowsPerPage,
                    searchData?.data?.totalRecords || 0
                  )}{" "}
                  of {searchData?.data?.totalRecords || 0} results
                </div>
                <div>
                  <ThemeProvider theme={theme}>
                    <Stack spacing={2}>
                      <Pagination
                        count={Math.ceil(
                          (searchData?.data?.totalRecords || 0) / rowsPerPage
                        )}
                        page={currentPage}
                        shape="rounded"
                        color="standard"
                        onChange={handlePageChange}
                      />
                    </Stack>
                  </ThemeProvider>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default SearchCv;
